// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * compare.ts — a full-screen before/after comparison overlay with a draggable
 * divider. The left of the divider shows the original; the right shows the
 * compressed result. A Fit / 100% toggle lets you inspect actual pixels.
 *
 * The stage box is sized to the image's exact aspect ratio (no letterboxing) so
 * the percentage clip lines up perfectly with what the eye sees.
 */

import { formatBytes, sizeDeltaPct } from './image';
import type { CompressItem } from './types';
import { escapeHtml } from './ui';

let root: HTMLElement | null = null;
let cleanup: (() => void) | null = null;

export function isCompareOpen(): boolean {
  return root !== null;
}

export function closeCompare(): void {
  cleanup?.();
  cleanup = null;
  root?.remove();
  root = null;
  document.body.classList.remove('modal-open');
}

export function openCompare(item: CompressItem): void {
  if (!item.srcUrl || !item.outUrl) return;
  closeCompare();

  const srcW = item.srcWidth ?? 0;
  const srcH = item.srcHeight ?? 0;
  const delta = sizeDeltaPct(item.inputSize, item.outputSize ?? 0);
  const ext = (item.outName?.split('.').pop() ?? '').toUpperCase();
  const saved = delta <= 0;

  root = document.createElement('div');
  root.className = 'cmp-overlay';
  root.innerHTML = `
    <div class="cmp-topbar">
      <div class="cmp-titles">
        <span class="cmp-name" title="${escapeHtml(item.name)}">${escapeHtml(item.name)}</span>
        <span class="cmp-stats">
          <span class="cmp-stat"><span class="cmp-k">Original</span> ${srcW}×${srcH} · ${formatBytes(item.inputSize)}</span>
          <span class="cmp-arrow">→</span>
          <span class="cmp-stat"><span class="cmp-k">${ext}</span> ${item.width ?? srcW}×${item.height ?? srcH} · ${formatBytes(item.outputSize ?? 0)}</span>
          <span class="cmp-delta ${saved ? 'down' : 'up'}">${delta > 0 ? '+' : ''}${delta}%</span>
        </span>
      </div>
      <div class="cmp-tools">
        <button type="button" class="cmp-zoom" id="cmp-zoom" aria-label="Toggle zoom">Fit</button>
        <button type="button" class="cmp-close" id="cmp-close" aria-label="Close comparison">&times;</button>
      </div>
    </div>
    <div class="cmp-scroll" id="cmp-scroll">
      <div class="cmp-stage" id="cmp-stage">
        <img class="cmp-img cmp-after" id="cmp-after" src="${item.outUrl}" alt="Compressed preview" draggable="false" />
        <div class="cmp-before-wrap" id="cmp-before-wrap">
          <img class="cmp-img cmp-before" src="${item.srcUrl}" alt="Original preview" draggable="false" />
        </div>
        <div class="cmp-divider" id="cmp-divider">
          <span class="cmp-handle" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M9 7l-4 5 4 5M15 7l4 5-4 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
        </div>
        <span class="cmp-tag cmp-tag-left">Original</span>
        <span class="cmp-tag cmp-tag-right">Compressed</span>
      </div>
    </div>
    <div class="cmp-hint">Drag the divider · left is the original, right is compressed · press <kbd>Esc</kbd> to close</div>
  `;
  document.body.appendChild(root);
  document.body.classList.add('modal-open');

  const scroll = root.querySelector<HTMLElement>('#cmp-scroll')!;
  const stage = root.querySelector<HTMLElement>('#cmp-stage')!;
  const beforeWrap = root.querySelector<HTMLElement>('#cmp-before-wrap')!;
  const beforeImg = beforeWrap.querySelector<HTMLImageElement>('.cmp-before')!;
  const divider = root.querySelector<HTMLElement>('#cmp-divider')!;
  const zoomBtn = root.querySelector<HTMLButtonElement>('#cmp-zoom')!;

  let pos = 50; // divider position, 0–100
  let zoom: 'fit' | 'actual' = 'fit';

  const aspect = srcW && srcH ? srcW / srcH : 1;

  function layout(): void {
    // Available area inside the scroll region.
    const availW = scroll.clientWidth - 32;
    const availH = scroll.clientHeight - 32;
    let w: number;
    let h: number;
    if (zoom === 'actual' && srcW && srcH) {
      w = srcW;
      h = srcH;
    } else {
      w = availW;
      h = w / aspect;
      if (h > availH) {
        h = availH;
        w = h * aspect;
      }
    }
    const pw = Math.round(w);
    const ph = Math.round(h);
    stage.style.width = `${pw}px`;
    stage.style.height = `${ph}px`;
    // Pin the clipped "before" image to the full stage size so the percentage
    // clip reveals it without horizontal distortion.
    beforeImg.style.width = `${pw}px`;
    beforeImg.style.height = `${ph}px`;
  }

  function apply(): void {
    pos = Math.min(100, Math.max(0, pos));
    beforeWrap.style.width = `${pos}%`;
    divider.style.left = `${pos}%`;
  }

  function pointerToPos(clientX: number): void {
    const rect = stage.getBoundingClientRect();
    if (rect.width === 0) return;
    pos = ((clientX - rect.left) / rect.width) * 100;
    apply();
  }

  let dragging = false;
  const onDown = (e: PointerEvent) => {
    dragging = true;
    divider.setPointerCapture?.(e.pointerId);
    pointerToPos(e.clientX);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    pointerToPos(e.clientX);
  };
  const onUp = () => {
    dragging = false;
  };
  divider.addEventListener('pointerdown', onDown);
  divider.addEventListener('pointermove', onMove);
  divider.addEventListener('pointerup', onUp);
  divider.addEventListener('pointercancel', onUp);
  // Click anywhere on the stage to jump the divider there.
  stage.addEventListener('pointerdown', (e) => {
    if (e.target === divider || divider.contains(e.target as Node)) return;
    pointerToPos((e as PointerEvent).clientX);
  });

  const onResize = () => layout();
  window.addEventListener('resize', onResize);

  zoomBtn.addEventListener('click', () => {
    zoom = zoom === 'fit' ? 'actual' : 'fit';
    zoomBtn.textContent = zoom === 'fit' ? 'Fit' : '100%';
    scroll.classList.toggle('zoomed', zoom === 'actual');
    layout();
  });

  root.querySelector('#cmp-close')?.addEventListener('click', () => closeCompare());
  root.addEventListener('pointerdown', (e) => {
    if (e.target === root) closeCompare();
  });

  cleanup = () => {
    window.removeEventListener('resize', onResize);
  };

  // Initial layout after images have a chance to report natural size.
  layout();
  apply();
  requestAnimationFrame(layout);
}
